import { useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { X, ZoomIn, ZoomOut, RotateCcw, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useModal } from "@/contexts/ModalContext";

interface ImageViewerProps {
  images: string[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onIndexChange?: (index: number) => void;
}

export function ImageViewer({ images, currentIndex, isOpen, onClose, onIndexChange }: ImageViewerProps) {
  const transformWrapperRef = useRef<{ 
    resetTransform: () => void;
    zoomIn: () => void;
    zoomOut: () => void;
  } | null>(null);

  // Reset zoom when image changes
  useEffect(() => {
    if (transformWrapperRef.current) {
      transformWrapperRef.current.resetTransform();
    }
  }, [currentIndex]);

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0 && onIndexChange) {
      onIndexChange(currentIndex - 1);
    }
  }, [currentIndex, onIndexChange]);

  const handleNext = useCallback(() => {
    if (currentIndex < images.length - 1 && onIndexChange) {
      onIndexChange(currentIndex + 1);
    }
  }, [currentIndex, images.length, onIndexChange]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft" && currentIndex > 0) {
        handlePrevious();
      } else if (e.key === "ArrowRight" && currentIndex < images.length - 1) {
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, images.length, onClose, handlePrevious, handleNext]);

  // Register/unregister with ModalContext for centralized back button handling
  const { registerModal, unregisterModal } = useModal();
  
  useEffect(() => {
    if (!isOpen) return;

    // Image viewer has highest priority (3) since it's a full-screen overlay
    registerModal('image-viewer', onClose, 3);

    return () => {
      unregisterModal('image-viewer');
    };
  }, [isOpen, onClose, registerModal, unregisterModal]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/95"
      style={{ touchAction: 'auto' }}
    >
      {/* Image Container with Zoom/Pan - Full Screen */}
      <div className="absolute inset-0">
        <TransformWrapper
          initialScale={1}
          minScale={0.5}
          maxScale={4}
          doubleClick={{ 
            mode: "toggle", 
            step: 0.7,
            disabled: false 
          }}
          wheel={{ step: 0.1 }}
          panning={{ 
            disabled: false,
            velocityDisabled: false,
            excluded: []
          }}
          pinch={{ 
            disabled: false,
            step: 5
          }}
          centerOnInit
          limitToBounds={false}
          disablePadding={false}
          alignmentAnimation={{ disabled: false }}
          velocityAnimation={{ disabled: false }}
          onInit={(ref) => {
            transformWrapperRef.current = ref;
          }}
        >
          <TransformComponent
            wrapperClass="!w-full !h-full"
            contentClass="!w-full !h-full flex items-center justify-center"
            wrapperStyle={{
              width: '100%',
              height: '100%',
              touchAction: 'auto',
              cursor: 'grab',
              WebkitUserSelect: 'none',
              userSelect: 'none'
            }}
          >
            <img
              src={images[currentIndex]}
              alt={`Image ${currentIndex + 1}`}
              className="max-w-full max-h-full object-contain select-none"
              draggable={false}
              style={{ 
                touchAction: 'auto',
                pointerEvents: 'auto',
                WebkitUserDrag: 'none'
              }}
            />
          </TransformComponent>
        </TransformWrapper>
      </div>

      {/* Top Controls - With pointer-events-none on container */}
      <div className="absolute top-0 left-0 right-0 pt-8 px-4 pb-4 flex items-center justify-between z-[110] bg-gradient-to-b from-black/60 to-transparent pointer-events-none">
        <div className="text-white text-sm font-medium pointer-events-none">
          {currentIndex + 1} / {images.length}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onClose();
          }}
          className="text-white hover:bg-white/20 pointer-events-auto"
        >
          <X className="h-6 w-6" />
        </Button>
      </div>

      {/* Bottom Controls - Outside TransformWrapper */}
      <div className="absolute bottom-0 left-0 right-0 px-4 pt-4 pb-8 flex items-center justify-center gap-2 z-[110] bg-gradient-to-t from-black/60 to-transparent pointer-events-none">
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            transformWrapperRef.current?.zoomOut();
          }}
          className="text-white hover:bg-white/20 pointer-events-auto"
        >
          <ZoomOut className="h-5 w-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            transformWrapperRef.current?.resetTransform();
          }}
          className="text-white hover:bg-white/20 pointer-events-auto"
        >
          <RotateCcw className="h-5 w-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            transformWrapperRef.current?.zoomIn();
          }}
          className="text-white hover:bg-white/20 pointer-events-auto"
        >
          <ZoomIn className="h-5 w-5" />
        </Button>
      </div>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          {currentIndex > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePrevious();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-[110] p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors pointer-events-auto"
              aria-label="Previous image"
            >
              <ChevronLeft className="h-8 w-8" />
            </button>
          )}
          {currentIndex < images.length - 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-[110] p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors pointer-events-auto"
              aria-label="Next image"
            >
              <ChevronRight className="h-8 w-8" />
            </button>
          )}
        </>
      )}

      {/* Swipe Indicator (mobile hint) */}
      {images.length > 1 && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-white/60 text-xs pointer-events-none">
          Swipe or use arrows to navigate
        </div>
      )}
    </div>,
    document.body
  );
}
